<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { RcDropdown, RcDropdownItem, RcDropdownTrigger } from '@components/RcDropdown';

type ScopeOption = { value: string; label: string; disabled?: boolean };

export default defineComponent({
  components: {
    RcDropdown, RcDropdownItem, RcDropdownTrigger
  },
  props: {
    value:    { type: String, required: true },
    options:  { type: Array as PropType<ScopeOption[]>, default: () => [] },
    label:    { type: String, required: true },
    disabled: { type: Boolean, default: false },
  },
  emits:    ['update:value'],
  computed: {
    selectedLabel(): string {
      return this.options.find((option) => option.value === this.value)?.label || 'Selection unavailable';
    },
  },
  methods: {
    select(option: ScopeOption) {
      if (!this.disabled && !option.disabled) {
        this.$emit('update:value', option.value);
      }
    },
  },
});
</script>

<template>
  <div class="scope-filter">
    <RcDropdown
      :aria-label="label"
      placement="bottom-start"
    >
      <RcDropdownTrigger
        class="scope-trigger"
        variant="secondary"
        size="small"
        :disabled="disabled"
        :aria-label="`${label}: ${selectedLabel}`"
        :title="selectedLabel"
      >
        <span class="scope-value">{{ selectedLabel }}</span>
        <i
          class="icon icon-chevron-down"
          aria-hidden="true"
        />
      </RcDropdownTrigger>
      <template #dropdownCollection>
        <RcDropdownItem
          v-for="option in options"
          :key="option.value"
          :disabled="disabled || option.disabled"
          :title="option.label"
          class="scope-option"
          @click="select(option)"
        >
          <span>{{ option.label }}</span>
          <i
            v-if="option.value === value"
            class="icon icon-checkmark"
            aria-hidden="true"
          />
        </RcDropdownItem>
      </template>
    </RcDropdown>
  </div>
</template>

<style lang="scss" scoped>
.scope-filter { min-width: 150px; max-width: 100%; width: 190px; }
.scope-filter :deep(.v-popper) { min-width: 0; width: 100%; }
:deep(.scope-trigger) { display: flex; align-items: center; gap: 8px; min-width: 0; width: 100%; height: 36px; padding: 0 10px; border: 1px solid var(--dev-border, var(--input-border)); border-radius: 6px; background: var(--dev-surface, var(--input-bg)); color: var(--dev-ink, var(--body-text)); font-size: 12px; font-weight: 400; }
.scope-value { min-width: 0; flex: 1; text-align: left; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
:deep(.scope-trigger > .icon) { flex-shrink: 0; color: var(--dev-muted, var(--muted)); }
:deep(.dropdownTarget) { min-width: min(190px, calc(100vw - 32px)); max-width: min(380px, calc(100vw - 32px)); max-height: min(440px, calc(100vh - 100px)); }
:deep(.scope-option) { display: flex; gap: 12px; font-size: 12px; span { min-width: 0; flex: 1; white-space: normal; overflow-wrap: anywhere; } > .icon { color: var(--dev-accent, var(--primary)); } }
@media (max-width: 480px) { .scope-filter { width: 100%; min-width: 0; } }
</style>
